
import React, { useState, useEffect, useCallback } from 'react';
import { Expenses as API, Customers, Company, TeamMembers } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = {
  customer_id: '', purpose: '',
  from_type: 'home', from_customer_id: '',
  to_type: 'customer', to_customer_id: '',
  kms: '', amt: '', notes: '',
  customer_expectation: '', next_followup_date: '',
  loc: '',
};

const STATUS_META = {
  pending_manager: { label: 'Pending Approval',     color: '#92400e', bg: '#fef3c7' },
  pending_finance: { label: 'Approved by Manager',  color: '#1d4ed8', bg: '#dbeafe' },
  paid:            { label: 'Paid',                 color: '#166534', bg: '#dcfce7' },
};

const getStatus = (e) => (e.fin_id !== "0") ? 'paid' : (e.ac_id !== "0") ? 'pending_finance' : 'pending_manager';

export default function Expenses() {
  const { user }  = useAuth();
  const [list, setList]       = useState([]);
  const [custs, setCusts]     = useState([]);
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [busy, setBusy]       = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const [gpsStatus, setGps]   = useState('idle'); // 'idle' | 'requesting' | 'granted' | 'denied'
  const [gpsMsg, setGpsMsg]   = useState('');

  // ── History drill-down ────────────────────────────────────
  const [historyOpen, setHistoryOpen]     = useState(false);
  const [historyExecId, setHistoryExecId] = useState(''); // for manager/finance picking a rep

  const captureGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGps('denied');
      setGpsMsg('GPS not supported on this device — cannot submit.');
      return;
    }
    setGps('requesting');
    setGpsMsg('Requesting your location…');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(f => ({ ...f, loc: `${pos.coords.latitude},${pos.coords.longitude}` }));
        setGps('granted');
        setGpsMsg('✓ Location captured');
      },
      () => {
        setGps('denied');
        setGpsMsg('Location permission denied. Please enable location access to submit.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  useEffect(() => {
    if (showNew) captureGPS();
  }, [showNew, captureGPS]);

  const load = useCallback(() => {
    setBusy(true);
    const calls = [API.list(), Customers.list(), Company.get()];
    // Only managers/finance need the team roster, for the history picker
    if (user.ryts == 1 || user.ryts == 3) calls.push(TeamMembers.list('y'));

    Promise.all(calls)
      .then(([exp, customers, corp, team]) => {
        setList(exp);
        setCusts(customers);
        setCompany(corp);
        if (team) setMembers(team);
      })
      .catch(console.error)
      .finally(() => setBusy(false));
  }, [user.ryts]);
  useEffect(load, [load]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // ── Location option list: Home / Corp / every Customer ──────
  const locationOptions = [
    { type: 'home', id: '', label: '🏠 Home' },
    { type: 'corp', id: '', label: `🏢 ${company?.business || 'Corporate Office'}` },
    ...custs.map(c => ({ type: 'customer', id: String(c.id), label: `👤 ${c.company}` })),
  ];

  const encodeLoc = (type, id) => type === 'customer' ? `customer:${id}` : type;
  const decodeLoc = (val) => {
    if (val.startsWith('customer:')) return { type: 'customer', id: val.split(':')[1] };
    return { type: val, id: '' };
  };

  const setFrom = e => {
    const { type, id } = decodeLoc(e.target.value);
    setForm(f => ({ ...f, from_type: type, from_customer_id: id }));
  };
  const setTo = e => {
    const { type, id } = decodeLoc(e.target.value);
    setForm(f => ({
      ...f, to_type: type, to_customer_id: id,
      customer_id: type === 'customer' ? id : f.customer_id,
    }));
  };

  const routeLabel = (type, id) => {
    if (type === 'home') return '🏠 Home';
    if (type === 'corp') return `🏢 ${company?.business || 'Corporate Office'}`;
    const c = custs.find(cc => String(cc.id) === String(id));
    return c ? `👤 ${c.company}` : '👤 Customer';
  };

  const custName = id => custs.find(c => c.id == id)?.company || (id ? `Customer ${id}` : '—');
  const execName = id => members.find(m => m.id == id)?.name || `User ${id}`;

  const submit = async () => {
    if (!form.purpose || !form.kms || !form.amt) {
      setErr('Purpose, Distance and Amount are required'); return;
    }
    setSaving(true); setErr('');
    try {
      await API.submit({
        ...form,
        depart: routeLabel(form.from_type, form.from_customer_id),
        arrive: routeLabel(form.to_type, form.to_customer_id),
        corp_id: user.corp_id,
        user_id: user.id,
      });
      setShowNew(false);
      setForm(EMPTY);
      setGps('idle');
      setGpsMsg('');
      load();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  const approve = async (id, stage) => {
    try {
      if (stage === 'manager') await API.approveManager(id, user.id);
      else await API.approveFinance(id, user.id);
      load();
    } catch (ex) { alert(ex.message); }
  };

  const reject = async (id, stage) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    try {
      if (stage === 'manager') await API.rejectManager(id, user.id, reason);
      else await API.rejectFinance(id, user.id, reason);
      load();
    } catch (ex) { alert(ex.message); }
  };

  // ── Role-scoped main list ────────────────────────────────────
  // Sales rep's API call already returns only their own rows (backend
  // filters by user_id when ryts==2), so no client-side user filter
  // is needed here — just the "hide Paid" rule.
  const visibleList = list.filter(e => {
    const status = getStatus(e);
    if (user.ryts == 2) return status !== 'paid';                 // sales: hide paid
    if (user.ryts == 1) return status !== 'paid';                 // manager: hide paid
    if (user.ryts == 3) return status === 'pending_finance';      // finance: only awaiting payment
    return true;                                                   // admin/other: everything
  });

  // ── History list ──────────────────────────────────────────────
  // Sales rep sees their own full history (list is already scoped
  // to them by the backend). Manager/finance pick an executive and
  // see that person's full history across all statuses.
  const historyList = user.ryts == 2
    ? list
    : list.filter(e => String(e.user_id) === String(historyExecId));

  const openHistory = () => {
    setHistoryExecId(user.ryts == 2 ? String(user.id) : '');
    setHistoryOpen(true);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">📍 Visits & Expenses</div>
        <button className="btn btn-ghost" onClick={openHistory}>🕓 History</button>
        <button className="btn btn-primary" onClick={() => { setShowNew(true); setErr(''); }}>+ Log Visit</button>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{err}</div>}

      {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
        visibleList.length === 0 ? <div className="empty">Nothing pending here right now</div> :
        visibleList.map(e => {
          const status = getStatus(e);
          const meta = STATUS_META[status];
          return (
            <div key={e.id} className="list-row" style={{ marginBottom: '.5rem' }}>
              <div className="row-main">
                <div className="row-title">
                  {routeLabel(e.from_type, e.from_customer_id)} → {routeLabel(e.to_type, e.to_customer_id)}
                </div>
                <div className="row-sub">
                  {e.purpose}
                  {e.customer_id ? ` · ${custName(e.customer_id)}` : ''}
                  {' · '}{e.kms} km · ₹{e.amt}
                  {e.dtime ? ` · ${e.dtime.slice(0, 10)}` : ''}
                  {(user.ryts == 1 || user.ryts == 3) && e.user_id ? ` · ${execName(e.user_id)}` : ''}
                </div>
                {e.notes && <div style={{ color: 'var(--faint)', fontSize: '.78rem', marginTop: 2 }}>{e.notes}</div>}
                {e.customer_expectation && (
                  <div style={{ color: 'var(--muted)', fontSize: '.78rem', marginTop: 2 }}>
                    💬 Expects: {e.customer_expectation}
                  </div>
                )}
                {e.next_followup_date && (
                  <div style={{ fontSize: '.78rem', marginTop: 4, color: 'var(--accent)' }}>
                    📅 Follow-up logged for {e.next_followup_date}
                  </div>
                )}
              </div>
              <div className="row-actions">
                <span className="badge" style={{
                  background: meta.bg, color: meta.color,
                  border: `1px solid ${meta.color}40`, padding: '2px 8px',
                  borderRadius: 12, fontSize: '.75rem', fontWeight: 600,
                }}>
                  {meta.label}
                </span>

                {status === 'pending_manager' && user.ryts == 1 && (
                  <>
                    <button className="btn btn-sm btn-primary" onClick={() => approve(e.id, 'manager')}>✓ Approve</button>
                    {/* <button className="btn btn-sm btn-danger" onClick={() => reject(e.id, 'manager')}>✗ Reject</button> */}
                  </>
                )}

                {status === 'pending_finance' && user.ryts == 3 && (
                  <>
                    <button className="btn btn-sm btn-success" onClick={() => approve(e.id, 'finance')}>✓ Pay Out</button>
                    {/* <button className="btn btn-sm btn-danger" onClick={() => reject(e.id, 'finance')}>✗ Reject</button> */}
                  </>
                )}
              </div>
            </div>
          );
        })
      }

      {/* Log Visit Modal */}
      {showNew && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Log Visit</span>
              <button className="btn-icon" onClick={() => setShowNew(false)}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>
                From *
                <select value={encodeLoc(form.from_type, form.from_customer_id)} onChange={setFrom}>
                  {locationOptions.map(o => (
                    <option key={`from-${o.type}-${o.id}`} value={encodeLoc(o.type, o.id)}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label>
                To *
                <select value={encodeLoc(form.to_type, form.to_customer_id)} onChange={setTo}>
                  {locationOptions.map(o => (
                    <option key={`to-${o.type}-${o.id}`} value={encodeLoc(o.type, o.id)}>{o.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <label style={{ marginBottom: '1rem' }}>
              Purpose / Description *
              <input value={form.purpose} onChange={set('purpose')} placeholder="e.g. Product demo at client site" autoFocus />
            </label>
            {form.to_type !== 'customer' && (
              <label style={{ marginBottom: '1rem' }}>
                Related Customer <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span>
                <select value={form.customer_id} onChange={set('customer_id')}>
                  <option value="">-- None / General --</option>
                  {custs.map(c => <option key={c.id} value={c.id}>{c.company} — {c.name}</option>)}
                </select>
              </label>
            )}
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Distance (km) *<input type="number" value={form.kms} onChange={set('kms')} min="0" /></label>
              <label>Amount (₹) *   <input type="number" value={form.amt} onChange={set('amt')} min="0" /></label>
            </div>
            <label style={{ marginBottom: '1rem' }}>
              Notes
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </label>
            <label style={{ marginBottom: '1rem' }}>
              What does the customer expect after this meeting?
              <textarea rows={2} value={form.customer_expectation} onChange={set('customer_expectation')}
                placeholder="e.g. Revised quote with 10% discount by Friday" />
            </label>
            <label style={{ marginBottom: '1.2rem' }}>
              Next Follow-up Date <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span>
              <input type="date" value={form.next_followup_date} onChange={set('next_followup_date')} />
            </label>
            <div style={{ marginBottom: '1.2rem' }}>
              {gpsStatus === 'requesting' && <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>📍 {gpsMsg}</span>}
              {gpsStatus === 'granted'    && <span style={{ fontSize: '.78rem', color: 'var(--green)' }}>{gpsMsg}</span>}
              {gpsStatus === 'denied' && (
                <div>
                  <span style={{ fontSize: '.78rem', color: 'var(--red)' }}>{gpsMsg}</span>
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={captureGPS}>Retry</button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={saving || gpsStatus !== 'granted'}
                title={gpsStatus !== 'granted' ? 'Location access required to submit' : ''}
              >
                {saving ? 'Submitting…' : gpsStatus === 'granted' ? 'Log Visit' : 'Waiting for location…'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyOpen && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setHistoryOpen(false)}>
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <span className="modal-title">
                🕓 {user.ryts == 2 ? 'My Expense History' : 'Executive Expense History'}
              </span>
              <button className="btn-icon" onClick={() => setHistoryOpen(false)}>✕</button>
            </div>

            {user.ryts != 2 && (
              <label style={{ marginBottom: '1rem' }}>
                Select Executive
                <select value={historyExecId} onChange={e => setHistoryExecId(e.target.value)}>
                  <option value="">-- Select --</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
            )}

            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {!historyExecId ? (
                <div className="empty">Select an executive to view their history</div>
              ) : historyList.length === 0 ? (
                <div className="empty">No expense records found</div>
              ) : (
                historyList.map(e => {
                  const status = getStatus(e);
                  const meta = STATUS_META[status];
                  return (
                    <div key={e.id} className="list-row" style={{ marginBottom: '.5rem' }}>
                      <div className="row-main">
                        <div className="row-title">
                          {routeLabel(e.from_type, e.from_customer_id)} → {routeLabel(e.to_type, e.to_customer_id)}
                        </div>
                        <div className="row-sub">
                          {e.purpose}
                          {e.customer_id ? ` · ${custName(e.customer_id)}` : ''}
                          {' · '}{e.kms} km · ₹{e.amt}
                          {e.dtime ? ` · ${e.dtime.slice(0, 10)}` : ''}
                        </div>
                        {status === 'paid' && (
                          <div style={{ color: 'var(--green)', fontSize: '.75rem', marginTop: 2 }}>
                            ✓ Manager: {e.ac_dt} · Paid: {e.fin_date}
                          </div>
                        )}
                      </div>
                      <div className="row-actions">
                        <span className="badge" style={{
                          background: meta.bg, color: meta.color,
                          border: `1px solid ${meta.color}40`, padding: '2px 8px',
                          borderRadius: 12, fontSize: '.75rem', fontWeight: 600,
                        }}>
                          {meta.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setHistoryOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
